"use client"

import { useState, useEffect, useSyncExternalStore, useCallback } from 'react'

import { readStorage, writeStorage } from '@/lib/safe-storage'

// Shared no-op subscribe for useSyncExternalStore-based "client vs server"
// hooks. These values never change after the initial client read, so there is
// nothing to subscribe to — React only needs the getSnapshot/getServerSnapshot
// pair to pick the correct value on the server vs. after hydration.
const emptySubscribe = () => () => {}

/**
 * Hook that returns true after the component has mounted on the client.
 * Useful for deferring client-only rendering to avoid hydration mismatches.
 *
 * Backed by {@link useIsClient} (useSyncExternalStore) — false during SSR and
 * the hydration render, true on the client thereafter. No setState-in-effect.
 *
 * @example
 * ```tsx
 * const hasMounted = useHasMounted()
 *
 * // Don't render client-dependent content until mounted
 * if (!hasMounted) return <Skeleton />
 *
 * return <ClientOnlyContent />
 * ```
 */
export function useHasMounted(): boolean {
  return useIsClient()
}

/**
 * Returns serverValue during SSR and initial hydration,
 * then switches to clientValue after mount.
 *
 * Implemented with `useSyncExternalStore`: the server snapshot is
 * `serverValue` (used for SSR + the hydration render so markup matches),
 * then React switches to `getClientValue()` on the client. This is the
 * React-Compiler-safe replacement for the old setState-in-effect approach.
 *
 * IMPORTANT: `getClientValue` is the store's `getSnapshot` — React calls it
 * on every render and bails out only when it returns a value that is `===`
 * the previous one. Pass a function that returns a **primitive** (boolean,
 * number, string) or a **referentially stable** object. Returning a fresh
 * object/array literal on every call will cause an infinite render loop.
 *
 * @example
 * ```tsx
 * // Show neutral state on server, actual preference on client
 * const prefersReducedMotion = useClientValue(
 *   false, // server value
 *   () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
 * )
 * ```
 */
export function useClientValue<T>(
  serverValue: T,
  getClientValue: () => T
): T {
  return useSyncExternalStore(
    emptySubscribe,
    getClientValue,
    () => serverValue
  )
}

/**
 * Safely reads from localStorage after component mounts.
 * Returns defaultValue during SSR and before mount.
 *
 * @example
 * ```tsx
 * const [volume, setVolume] = useSafeLocalStorage('audio-volume', 0.7)
 * ```
 */
export function useSafeLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Start with default value (safe for SSR)
  // eslint-disable-next-line react-doctor/no-derived-state -- intentional: defaultValue is an initial seed, not derived; localStorage sync happens in effect
  const [storedValue, setStoredValue] = useState<T>(defaultValue)

  // Load from localStorage after mount
  useEffect(() => {
    const item = readStorage(key)
    if (item === null) return
    try {
      const parsed = JSON.parse(item)
      // This is intentional - we need to sync state after hydration
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStoredValue(parsed)
    } catch {
      // Corrupt JSON — keep the default rather than throwing in the effect.
    }
  }, [key])

  // Setter that also updates localStorage (a no-op when it's unavailable —
  // state still advances so the current session behaves normally).
  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStoredValue(prev => {
      const nextValue = value instanceof Function ? value(prev) : value
      writeStorage(key, JSON.stringify(nextValue))
      return nextValue
    })
  }, [key])

  return [storedValue, setValue]
}

/**
 * Alternative to useHasMounted using useSyncExternalStore for React 18+.
 * This is the recommended approach as it's specifically designed for this use case.
 */
const getSnapshot = () => true
const getServerSnapshot = () => false

export function useIsClient(): boolean {
  return useSyncExternalStore(emptySubscribe, getSnapshot, getServerSnapshot)
}

/**
 * Returns a date formatted for the user's locale, but only after mount.
 * During SSR, returns the ISO date string to avoid hydration mismatch.
 *
 * @example
 * ```tsx
 * const formattedDate = useFormattedDate(createdAt, {
 *   dateStyle: 'medium'
 * })
 * ```
 */
export function useFormattedDate(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const isClient = useIsClient()

  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Return ISO format on server, locale format on client
  if (!isClient) {
    return dateObj.toISOString().split('T')[0]
  }

  return dateObj.toLocaleDateString(undefined, options)
}

/**
 * Returns whether a date is within a certain number of hours from now.
 * Only calculates on the client to avoid hydration mismatch.
 * Returns false during SSR.
 *
 * @example
 * ```tsx
 * const isNew = useIsRecent(createdAt, 24) // within last 24 hours
 * ```
 */
export function useIsRecent(
  date: string | Date | null | undefined,
  withinHours: number
): boolean {
  const isClient = useIsClient()
  // eslint-disable-next-line react-doctor/no-derived-state -- intentional: isRecent cannot be computed synchronously during SSR; hydration guard requires effect
  const [isRecent, setIsRecent] = useState(false)

  // This effect syncs state based on external factors (date/time) after hydration
  // eslint-disable-next-line react-doctor/no-adjust-state-on-prop-change -- intentional: isClient is a hydration flag, not a prop; date-based recency must run client-side
  useEffect(() => {
    if (!date || !isClient) {
      setIsRecent(false) // eslint-disable-line react-hooks/set-state-in-effect
      return
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const hoursDiff = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60)
    setIsRecent(hoursDiff < withinHours)
  }, [date, withinHours, isClient])

  return isRecent
}
